import type { Id, Iso8601, ModelMeta } from './common';

/**
 * Roles are the *primary* axis of the app's experience.
 * Every page chooses what it shows based on the active role.
 */
export type Role =
  | 'admin'
  | 'scorekeeper'
  | 'referee'
  | 'captain'
  | 'player'
  | 'fan'
  | 'staff';

export const ALL_ROLES: Role[] = [
  'admin',
  'scorekeeper',
  'referee',
  'captain',
  'player',
  'fan',
  'staff',
];

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  scorekeeper: 'Scorekeeper',
  referee: 'Referee',
  captain: 'Captain',
  player: 'Player',
  fan: 'Fan',
  staff: 'Staff',
};

export type DirectoryStatus = 'active' | 'inactive' | 'archived';
export type PreferredContactMethod = 'email' | 'sms' | 'in_app' | 'none';
export type ProfileAttributeValue = string | number | boolean | null;

export interface ContactPreferences {
  emailOptOut?: boolean;
  smsOptIn?: boolean;
  smsOptOut?: boolean;
  smsMarketingOptIn?: boolean;
  inAppOptOut?: boolean;
  lastVerifiedAt?: Iso8601;
}

/**
 * Profile = the person. One Profile can hold many Roles across many Tournaments.
 * Tournament-specific role context lives on RoleAssignment, not here.
 */
export interface Profile extends ModelMeta {
  readonly kind: 'profile';
  displayName: string;
  email?: string;
  phone?: string;
  avatarUrl?: string;
  /** Default landing role (used by the dev role switcher and first-run UX). */
  defaultRole: Role;
  /** Free-form bio surfaced on the Player Dashboard. */
  bio?: string;
  pronouns?: string;
  /** Persistent global roles independent of any tournament. */
  globalRoles: Role[];
  /** Directory lifecycle state. Archived people stay attached to history. */
  directoryStatus?: DirectoryStatus;
  preferredContactMethod?: PreferredContactMethod;
  contactPreferences?: ContactPreferences;
  /** Flexible league-specific attributes used for segmentation and comms. */
  attributes?: Record<string, ProfileAttributeValue>;
  /** Where this record came from: manual, registration, roster import, etc. */
  source?: string;
  tags?: string[];
  lastVerifiedAt?: Iso8601;
  archivedAt?: Iso8601;
}

/** Convenience alias — a logged-in (or simulated) user is just a Profile. */
export type User = Profile;

/** Lightweight reference used when we don't need the whole Profile. */
export interface ProfileRef {
  id: Id;
  displayName: string;
  avatarUrl?: string;
}
